import { useEffect } from 'react';

export interface HotkeyOptions {
  /** Lower-case key, e.g. 's', 'k', 'arrowdown'. */
  key: string;
  meta?: boolean;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  /** When false, do nothing. */
  enabled?: boolean;
  /**
   * Whether to fire when focus is inside an editable element.
   * Default false — most app shortcuts should defer to the input.
   */
  allowInInput?: boolean;
}

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (EDITABLE_TAGS.has(target.tagName)) return true;
  if (target.isContentEditable) return true;
  return false;
}

// Single-key + modifier shortcut binding. Treats Meta (⌘) and Ctrl as
// interchangeable — analysts on macOS reach for ⌘ S, on Windows for Ctrl+S.
export function useHotkey(opts: HotkeyOptions, handler: (e: KeyboardEvent) => void): void {
  const { key, meta, ctrl, shift = false, alt = false, enabled = true, allowInInput = false } = opts;
  useEffect(() => {
    if (!enabled) return;
    function onKey(e: KeyboardEvent) {
      if (e.key.toLowerCase() !== key.toLowerCase()) return;
      // Treat ⌘ (mac) and Ctrl (win/linux) as equivalent when either is requested.
      if (meta || ctrl) {
        const modifierHeld = e.metaKey || e.ctrlKey;
        if (!modifierHeld) return;
      } else if (e.metaKey || e.ctrlKey) {
        return;
      }
      if (!!shift !== e.shiftKey) return;
      if (!!alt !== e.altKey) return;
      if (!allowInInput && isEditableTarget(e.target)) return;
      handler(e);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [key, meta, ctrl, shift, alt, enabled, allowInInput, handler]);
}
